import React, { useEffect, useContext, useState, useMemo } from 'react';
import * as Styled from './style';
import { Typography, Grid } from '@mui/material';
import { useMetricsCriteria } from 'hooks/useMetricsCriteria';
import { useApplicationPerformance } from 'hooks/application-performance-hook';
import { constants } from 'constants/Constants';
import KpiDefinitionDrawer from 'components/KpiDefinitionDrawer/KpiDefinitionDrawer';
import KpiDefinitionData from 'service/KpiDefinitionData';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import { helperFunctions } from 'utils/helperFunctions';
import { ThemeContext } from '../../../context/ThemeContext';
import SLAAnalysis from '../../../components/SLAAnalysis/SLAAnalysis';
import Loader from '../../../components/Loader/Loader';
import { useLayoutNavigation } from '../../../hooks/layout-navigation-hook';
import { getRealTimeOnboardingConfigProps } from '../../../utils/dynamicKPIs';
import KpiMetricsForThree from '../../../components/KpiMetricsForThree/KpiMetricsForThree';
import KPIMetricsForTwo from '../../../components/KPIMetricsForTwo';
import { formatApiResponseTimeWithSeconds } from 'components/ApplicationPerformanceOverview/helper';

function AppPerformanceOverviewCommon(): React.JSX.Element {
  const { operationalMetricCriteria } = useMetricsCriteria();
  const [applicationNames] = useState(
    constants?.applicationPerformance?.assignedGroupNames
  );

  const {
    selectedProgram,
    selectedProduct,
    selectedSector,
    selectedMarket,
    startUTCDate,
    endUTCDate,
    categories,
  } = useContext(FilterContext);

  const [appPerformanceProps, setAppPerformanceProps] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const { getRealTimeOnboardingConfigData, realTimeOnboardingConfigData } =
    useLayoutNavigation();

  useEffect(() => {
    if (selectedProduct?.productId) {
      getRealTimeOnboardingConfigData({
        variables: { productId: selectedProduct?.productId },
      });
    }
  }, [selectedProduct?.productId, getRealTimeOnboardingConfigData]);

  useEffect(() => {
    if (realTimeOnboardingConfigData) {
      const { appPerfProps } = getRealTimeOnboardingConfigProps(
        realTimeOnboardingConfigData
      );
      setAppPerformanceProps((prev) => ({ ...prev, ...appPerfProps }));
      setIsReady(true);
    }
  }, [realTimeOnboardingConfigData]);

  const showSLAAnalysis = appPerformanceProps?.getIncidentMonitoringSlaAnalysis;
  const showIncidentMonitoringOverview =
    appPerformanceProps?.getIncidentMonitoringOverview;

  const applicationMetricCriteria = useMemo(
    () => ({
      programId: selectedProgram === null ? 2 : selectedProgram?.programId,
      productId: selectedProduct === null ? 1 : selectedProduct?.productId,
      sectorId: selectedSector === null ? 1 : selectedSector?.sectorId,
      marketId: selectedMarket === null ? null : selectedMarket?.marketId,
      categories: categories,
      startDate: startUTCDate,
      endDate: endUTCDate,
      applicationNames,
    }),
    [
      selectedProgram,
      selectedProduct,
      selectedSector,
      selectedMarket,
      startUTCDate,
      endUTCDate,
      categories,
    ]
  );
  const { mode } = useContext(ThemeContext);
  // const [showIncidentMonitoringDetails, setShowIncidentMonitoringDetails] =
  //   useState(false);
  const {
    getIncidentMonitoringData,
    incidentMonitoringData,
    incidentMonitoringLoading,
    getNRMAppPerformanceScoreCardData,
    nrmAppPerfScoreCardData,
    nrmAppPerformanceScoreCardLoading,
  } = useApplicationPerformance();

  useEffect(() => {
    const updatedApplicationMetrics = { ...applicationMetricCriteria };
    updatedApplicationMetrics.applicationNames =
      selectedProduct?.productId === constants.products.PepsiConnect
        ? constants?.applicationPerformance?.assignedGroupNames
        : [];

    getIncidentMonitoringData({
      variables: {
        applicationMetricCriteria: updatedApplicationMetrics,
      },
    });
  }, [getIncidentMonitoringData, applicationMetricCriteria]);

  useEffect(() => {
    const scoreCardParams = {
      ...operationalMetricCriteria,
    };
    getNRMAppPerformanceScoreCardData({
      variables: {
        applicationMetricCriteria: scoreCardParams,
      },
    });
  }, [
    selectedProgram,
    selectedProduct,
    operationalMetricCriteria,
    getNRMAppPerformanceScoreCardData,
  ]);

  const { getValueAbbreviationParser, formatTimeWithSeconds } =
    helperFunctions();

  const renderSlaAnalysis = () => (
    <SLAAnalysis
      incidentMonitoringSlaAnalysisData={
        incidentMonitoringData?.incidentMonitoringSlaAnalysisData
      }
      paddingTop={'0px'}
      paddingBottomInnerContainer={'0px'}
      isAppPerform={false}
    />
  );

  const kpiMetricsData = [
    {
      data: getValueAbbreviationParser(
        incidentMonitoringData?.incidentMonitoringOverviewData?.incidentCount
      ),
      text: 'Incident Count',
    },
    {
      data: formatTimeWithSeconds(
        incidentMonitoringData?.incidentMonitoringOverviewData?.responseTime
      ),
      text: 'Mean Time To Respond',
    },
    {
      data: formatTimeWithSeconds(
        incidentMonitoringData?.incidentMonitoringOverviewData?.resolutionTime
      ),
      text: 'Mean Time To Resolve',
    },
  ];

  const kpiMetricsScorecardData = [
    {
      data: formatApiResponseTimeWithSeconds(
        nrmAppPerfScoreCardData?.avgApiAppResponseTime
      ),
      text: 'Avg API Response Time',
    },
    {
      data: getValueAbbreviationParser(
        nrmAppPerfScoreCardData?.totalNoOfErrors
      ),
      text: 'Total Number of Errors',
    },
  ];

  function getGridLgValue(showSLAAnalysis, showIncidentMonitoringOverview) {
    if (showSLAAnalysis && showIncidentMonitoringOverview) {
      return 6; // Both sections visible
    } else {
      return 12; // Only one section visible
    }
  }

  return (
    <Styled.DTCOverviewContainer
      data-testid="appperformance-overview-container"
      mode={mode}
    >
      <Grid item xs={12}>
        <Typography className="header">
          {constants?.overview?.appPerformance}
          <KpiDefinitionDrawer
            title={constants?.overview?.appPerformance}
            KpiDefinitionList={
              KpiDefinitionData[selectedProduct?.productId]?.overview
                ?.applicationPerformance ?? []
            }
          />
        </Typography>
        <Grid item xs={12} className="card-panel">
          {incidentMonitoringLoading || nrmAppPerformanceScoreCardLoading ? (
            <Loader />
          ) : (
            <Grid container>
              {!isReady ? (
                <div
                  data-testid="app-perf-config-loader"
                  style={{ marginTop: '2%' }}
                >
                  <Loader />
                </div>
              ) : (
                <>
                  {showIncidentMonitoringOverview && (
                    <Grid
                      item
                      xs={12}
                      md={showSLAAnalysis ? 6 : 12}
                      sm={12}
                      lg={getGridLgValue(
                        showSLAAnalysis,
                        showIncidentMonitoringOverview
                      )}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        paddingLeft: '2%',
                        paddingBottom: '15px',
                      }}
                    >
                      <div style={{ marginBottom: '15px' }}>
                        <KPIMetricsForTwo
                          kpiMetricsData={kpiMetricsScorecardData}
                          isIncidentMonitoringOverview={true}
                        />
                      </div>
                      <div>
                        <KpiMetricsForThree
                          KpiMetricsData={kpiMetricsData}
                          isGSNBA={false}
                        />
                      </div>
                    </Grid>
                  )}

                  {showSLAAnalysis && (
                    <Grid
                      item
                      xs={12}
                      md={showIncidentMonitoringOverview ? 6 : 12}
                      sm={12}
                      lg={getGridLgValue(
                        showSLAAnalysis,
                        showIncidentMonitoringOverview
                      )}
                    >
                      <Grid className="card-panel noBorder">
                        {renderSlaAnalysis()}
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Styled.DTCOverviewContainer>
  );
}
export default AppPerformanceOverviewCommon;
